export type SecurityLevel = { algo: number; weights: number; sabotage: number }
export type VerificationLevel = number // 0-3

export type Datacenter = {
  name: string
  lat: number
  lng: number
  compute: number // in H100 equivalents
  secLevel: SecurityLevel
  verificationLevel: VerificationLevel
}

export type CountryWakeup = {
  country: string
  level: number // 0-5
}

export type ComputeData = {
  usFlow: number
  chinaFlow: number
  usStock: number
  chinaStock: number
}

export type AlignmentMetrics = {
  qacaarys: number
  timeWithAGI: number
  peopleWithPower: number
  giniCoefficient: number
}

export type EconomicMetrics = {
  computeUS: number
  computeChina: number
  computeRest: number
  robotsUS: number
  robotsChina: number
  robotsRest: number
  energyUS: string
  energyChina: string
  energyRest: string
  physicalLaborUS: number
  physicalLaborChina: number
  physicalLaborRest: number
  cognitiveLaborUS: number
  cognitiveLaborChina: number
  cognitiveLaborRest: number
}

export type AgendaStatus = { [key: string]: number } // 1-3 code

export type HudMetrics = {
  frontierProjectsUS: number
  frontierProjectsChina: number
  frontierProjectsOther: number
  transparencyFrontierProjects: number
  transparencyGovernments: number
  oversightAICompanies: number
  organizationalCultureFrontierProjects: number
  decisionmakingGovernments: number
  truthSeekingAI: number
  safetyResearcherPopulation: number
  slowdownAmount: number
  medianUSIncome: number
}

// One sub-criterion attached to a HUD metric. Each carries a 1-N rating
// (typically 1-5) sourced from rows in the spreadsheet snapshot whose
// 2nd column ends in " criteria".
export type HudMetricCriterion = {
  label: string
  value: number
}

export type HudMetricCriteriaMap = Partial<
  Record<keyof HudMetrics, HudMetricCriterion[]>
>

export type PlanAPhaseData = {
  date: string
  phase: string
  datacenters: Datacenter[]
  countryWakeup: CountryWakeup[]
  compute: ComputeData
  alignment: AlignmentMetrics
  economic: EconomicMetrics
  agendas: AgendaStatus
  leadingUSEC: number
  leadingChinaEC: number
  hudMetrics?: HudMetrics
  hudMetricCriteria?: HudMetricCriteriaMap
  rdMultiple?: [number, number] // [OpenBrain, China] speedup multipliers
  // Branch-plan (B/C/D/S) indicator overrides; absent on Plan A phases
  // except safetyTaxMultiplier/totalSlowdownYears, which Plan A also gets
  // (from the plan-a-side-panel sheet rows) so the panel can show either.
  employmentRatePct?: number
  safetyTaxMultiplier?: number
  // Years later that AI R&D milestones are reached vs an unconstrained
  // (Plan D) trajectory — the side panel's "Total Slowdown" reading.
  totalSlowdownYears?: number
  worldComputeH100e?: number
  // World humans still working (employment-collapsed) — when set, the labor
  // dot strip uses these counts and goes population-proportional vs AI.
  humanCogWorkers?: number
  humanPhysWorkers?: number
  // Side-panel AI copies count and per-copy speed (tok/s) on the branch
  // clock; the labor strip prefers these over the compute power law.
  aiCopies?: number
  aiSpeedTokS?: number
}

// Get data for a specific date (finds closest date <= target)
export function getDataForDate(allData: PlanAPhaseData[], targetDate: Date): PlanAPhaseData | null {
  const targetTime = targetDate.getTime()
  let closest: PlanAPhaseData | null = null
  for (const phase of allData) {
    const phaseTime = new Date(phase.date).getTime()
    if (phaseTime <= targetTime) {
      closest = phase
    } else {
      break
    }
  }
  return closest
}
